import axios from 'axios';
import config from '../config';
import { AuthResponse, User, CreateTaskRequest, UpdateCompletionRequest } from '../types';

class ApiService {
  private api = axios.create({
    baseURL: config.apiUrl,
    timeout: config.apiTimeout,
  });

  private token: string | null = localStorage.getItem('auth_token');

  constructor() {
    if (this.token) {
      this.api.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
    }

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('API: 401 error - token expired or invalid');
          this.handleTokenExpiration();
        }
        return Promise.reject(error);
      }
    );

    if (config.isDevelopment) {
      console.log(`🌐 API Service initialized: ${config.apiUrl}`);
    }
  }

  private handleTokenExpiration() {
    console.log('API: Handling token expiration');
    this.clearAuth();
    alert('Your session has expired. Please log in again to continue.');
    window.location.reload();
  }

  private setAuth(token: string, user: User) {
    console.log('API: Setting auth token');
    this.token = token;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_data', JSON.stringify(user));
  }

  private clearAuth() {
    console.log('API: Clearing auth data');
    this.token = null;
    delete this.api.defaults.headers.common['Authorization'];
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('selectedDate');
    localStorage.removeItem('taskData');
  }

  // Auth endpoints
  async register(userData: any): Promise<AuthResponse> {
    const response = await this.api.post('/api/auth/register', userData);
    if (response.data.success && response.data.token && response.data.user) {
      this.setAuth(response.data.token, response.data.user);
    }
    return response.data;
  }

  async login(credentials: { email: string; password: string }): Promise<AuthResponse> {
    console.log('API: Login attempt');
    const response = await this.api.post('/api/auth/login', credentials);
    if (response.data.success && response.data.token && response.data.user) {
      this.setAuth(response.data.token, response.data.user);
    }
    return response.data;
  }

  // OTP Login endpoints
  async requestOtp(phone: string): Promise<{ success: boolean; message?: string; debug_otp?: string }> {
    console.log('API: Requesting OTP for:', phone);
    const response = await this.api.post('/api/auth/login-otp', { phone });
    return response.data;
  }

  async verifyOtp(phone: string, otp: string): Promise<AuthResponse> {
    console.log('API: Verifying OTP');
    const response = await this.api.post('/api/auth/verify-otp', { phone, otp });
    if (response.data.success && response.data.token && response.data.user) {
      this.setAuth(response.data.token, response.data.user);
    }
    return response.data;
  }

  async getProfile(): Promise<{ user: User; stats: any }> {
    const response = await this.api.get('/api/auth/me');
    return response.data;
  }

  // Password Reset endpoints
  async forgotPassword(email: string) {
    const response = await this.api.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  async verifyResetToken(token: string) {
    const response = await this.api.get(`/api/auth/verify-reset-token/${token}`);
    return response.data;
  }

  async resetPassword(token: string, password: string) {
    const response = await this.api.post('/api/auth/reset-password', { token, password });
    return response.data;
  }

  // Export endpoints
  async getExportStatus() {
    const response = await this.api.get('/api/export/status');
    return response.data;
  }

  // Task endpoints
  async getTasks(date: string) {
    const response = await this.api.get(`/api/tasks?date=${date}`);
    return response.data;
  }

  async createTask(taskData: CreateTaskRequest) {
    const response = await this.api.post('/api/tasks', taskData);
    return response.data;
  }

  async updateTask(taskId: string, updateData: { 
    title?: string; 
    description?: string; 
    priority?: number;
    taskType?: string;
    scheduledHour?: number;
    startTime?: string;
    endTime?: string;
    estimatedDurationMinutes?: number;
    displayOrder?: number;
    scheduledDate?: string;
  }) {
    const response = await this.api.put(`/api/tasks/${taskId}`, updateData);
    return response.data;
  }

  async updateTaskCompletion(taskId: string, completionData: UpdateCompletionRequest) {
    const response = await this.api.patch(`/api/tasks/${taskId}/completion`, completionData);
    return response.data;
  }

  async deleteTask(taskId: string) {
    const response = await this.api.delete(`/api/tasks/${taskId}`);
    return response.data;
  }

  async rescheduleTask(taskId: string, data: { newDate: string; newHour?: number; newStartTime?: string; newEndTime?: string; newDurationMinutes?: number; taskType?: string }) {
    const response = await this.api.patch(`/api/tasks/${taskId}/reschedule`, data);
    return response.data;
  }

  // Analytics endpoints
  async getAnalyticsDashboard(days: number = 30) {
    const response = await this.api.get(`/api/analytics/dashboard?days=${days}`);
    return response.data;
  }

  async getAnalyticsInsights(days: number = 30) {
    const response = await this.api.get(`/api/analytics/insights?days=${days}`);
    return response.data;
  }

  // Export endpoints
  async exportDaily(date: string) {
    const response = await this.api.get(`/api/export/daily?date=${date}`, {
      responseType: 'blob'
    });
    return response;
  }

  async exportRange(startDate: string, endDate: string) {
    const response = await this.api.get(`/api/export/range?startDate=${startDate}&endDate=${endDate}`, {
      responseType: 'blob'
    });
    return response;
  }

  // Auth utilities
  logout() {
    console.log('API: Logout called');
    this.clearAuth();
  }

  isAuthenticated(): boolean {
    const hasToken = !!this.token;
    
    if (hasToken) {
      try {
        const userData = localStorage.getItem('user_data');
        const tokenData = localStorage.getItem('auth_token');
        
        if (!userData || !tokenData) {
          this.clearAuth();
          return false;
        }
      } catch (error) {
        console.log('API: Token validation error:', error);
        this.clearAuth();
        return false;
      }
    }
    
    console.log('API: isAuthenticated check:', hasToken);
    return hasToken;
  }

  getCurrentUser(): User | null {
    const userData = localStorage.getItem('user_data');
    const user = userData ? JSON.parse(userData) : null;
    console.log('API: getCurrentUser:', user?.username || 'none');
    return user;
  }
   // Time slot management
  async splitHour(date: string, hour: number) {
    const response = await this.api.post('/api/tasks/split-hour', { date, hour });
    return response.data;
  }

  async mergeHour(date: string, hour: number) {
    const response = await this.api.post('/api/tasks/merge-hour', { date, hour });
    return response.data;
  }

  // Notification settings
  async getNotificationSettings() {
    const response = await this.api.get('/api/notifications/settings');
    return response.data;
  }

  async updateNotificationSettings(settings: {
    notificationChannel?: string;
    defaultReminderEnabled?: boolean;
    defaultReminderMinutes?: number;
  }) {
    const response = await this.api.put('/api/notifications/settings', settings);
    return response.data;
  }

  // Recurring tasks
  async getRecurringTasks(activeOnly = true) {
    const response = await this.api.get(`/api/recurring-tasks?active=${activeOnly}`);
    return response.data;
  }

  async createRecurringTask(data: {
    title: string;
    description?: string;
    scheduledHour: number;
    label?: string;
    priority?: number;
    recurrenceType: 'daily' | 'weekly' | 'monthly' | 'custom';
    recurrenceDays?: number[];
    recurrenceDayOfMonth?: number;
    recurrenceInterval?: number;
    reminderEnabled?: boolean;
    reminderMinutesBefore?: number;
  }) {
    const response = await this.api.post('/api/recurring-tasks', data);
    return response.data;
  }

  async deleteRecurringTask(id: string) {
    const response = await this.api.delete(`/api/recurring-tasks/${id}`);
    return response.data;
  }

  async generateTasksForDate(date: string) {
    const response = await this.api.post('/api/recurring-tasks/generate', { date });
    return response.data;
  }

  async testTaskReminder(taskId: string, channel?: string) {
    const response = await this.api.post('/api/notifications/test-task-reminder', { taskId, channel });
    return response.data;
  }

  // Profile management
  async updateProfile(data: {
    firstName?: string;
    lastName?: string;
    phone?: string;
    timezone?: string;
  }) {
    const response = await this.api.put('/api/auth/profile', data);
    if (response.data.success && response.data.user) {
      // Update local storage with new user data
      const currentUser = this.getCurrentUser();
      if (currentUser) {
        const updatedUser = { ...currentUser, ...response.data.user };
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
      }
    }
    return response.data;
  }

  // Test notifications
  async testEmailNotification() {
    const response = await this.api.get('/api/notifications/test');
    return response.data;
  }

  async testWhatsAppNotification() {
    const response = await this.api.get('/api/notifications/test-whatsapp');
    return response.data;
  }

  // App Settings & Themes
  async getPublicSettings() {
    const response = await this.api.get('/api/app-settings/public');
    return response.data;
  }

  async getAvailableThemes() {
    const response = await this.api.get('/api/app-settings/themes');
    return response.data;
  }

  async updateUserTheme(themeSlug: string) {
    const response = await this.api.put('/api/app-settings/theme', { themeSlug });
    return response.data;
  }

  async getNotificationAccess() {
    const response = await this.api.get('/api/app-settings/notification-access');
    return response.data;
  }

  // Admin APIs
  async getAdminStats() {
    const response = await this.api.get('/api/admin/stats');
    return response.data;
  }

  async getAdminUsers(includeArchived = false) {
    const response = await this.api.get(`/api/admin/users?includeArchived=${includeArchived}`);
    return response.data;
  }

  async toggleUserActive(userId: string) {
    const response = await this.api.put(`/api/admin/users/${userId}/toggle-active`);
    return response.data;
  }

  async archiveUser(userId: string) {
    const response = await this.api.put(`/api/admin/users/${userId}/archive`);
    return response.data;
  }

  async restoreUser(userId: string) {
    const response = await this.api.put(`/api/admin/users/${userId}/restore`);
    return response.data;
  }

  async deleteUserPermanently(userId: string) {
    const response = await this.api.delete(`/api/admin/users/${userId}`);
    return response.data;
  }

  async toggleUserWhatsApp(userId: string) {
    const response = await this.api.put(`/api/admin/users/${userId}/toggle-whatsapp`);
    return response.data;
  }

  async toggleUserPremiumThemes(userId: string) {
    const response = await this.api.put(`/api/admin/users/${userId}/toggle-premium-themes`);
    return response.data;
  }

  async getAdminSettings() {
    const response = await this.api.get('/api/admin/settings');
    return response.data;
  }

  async updateAdminSetting(key: string, value: string) {
    const response = await this.api.put(`/api/admin/settings/${key}`, { value });
    return response.data;
  }

  async getAdminThemes() {
    const response = await this.api.get('/api/admin/themes');
    return response.data;
  }

  async setDefaultTheme(themeSlug: string) {
    const response = await this.api.put('/api/admin/settings/default-theme', { themeSlug });
    return response.data;
  }

  // Bucket List APIs
  async getBucketList(includeCompleted = false) {
    const response = await this.api.get(`/api/bucket-list?includeCompleted=${includeCompleted}`);
    return response.data;
  }

  async getBucketListStats() {
    const response = await this.api.get('/api/bucket-list/stats');
    return response.data;
  }

  async createBucketListItem(data: { title: string; description?: string; priority?: number }) {
    const response = await this.api.post('/api/bucket-list', data);
    return response.data;
  }

  async updateBucketListItem(id: string, data: { title?: string; description?: string; priority?: number; is_completed?: boolean }) {
    const response = await this.api.put(`/api/bucket-list/${id}`, data);
    return response.data;
  }

  async assignBucketListDate(id: string, date: string, hour?: number) {
    const response = await this.api.post(`/api/bucket-list/${id}/assign`, { date, hour });
    return response.data;
  }

  async deleteBucketListItem(id: string) {
    const response = await this.api.delete(`/api/bucket-list/${id}`);
    return response.data;
  }

  // Calendar APIs
  async getCalendarOverview(startDate: string, endDate: string) {
    const response = await this.api.get(`/api/calendar/overview?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  }

  async getPendingTasksSummary() {
    const response = await this.api.get('/api/calendar/pending');
    return response.data;
  }

  async getRecurringTasksStats() {
    const response = await this.api.get('/api/calendar/recurring-stats');
    return response.data;
  }

  // Admin Notification Management APIs
  async getAdminUsersNotifications() {
    const response = await this.api.get('/api/admin/users/notifications');
    return response.data;
  }

  async updateUserNotifications(userId: string, settings: Record<string, any>) {
    const response = await this.api.put(`/api/admin/users/${userId}/notifications`, settings);
    return response.data;
  }

  async sendTestNotification(userId: string, type: string, channel: string) {
    const response = await this.api.post(`/api/admin/users/${userId}/test-notification`, { type, channel });
    return response.data;
  }

  async getNotificationLogs(params?: { userId?: string; type?: string; channel?: string; status?: string; limit?: number }) {
    const queryString = new URLSearchParams(params as any).toString();
    const response = await this.api.get(`/api/admin/notification-logs?${queryString}`);
    return response.data;
  }

  async triggerNotificationProcessing() {
    const response = await this.api.post('/api/admin/notifications/process');
    return response.data;
  }

  // Category Management APIs
  async getCategories() {
    const response = await this.api.get('/api/categories');
    return response.data;
  }

  async getCategoryLimit() {
    const response = await this.api.get('/api/categories/limit');
    return response.data;
  }

  async createCategory(data: { name: string; color: string; textColor?: string }) {
    const response = await this.api.post('/api/categories', data);
    return response.data;
  }

  async updateCategory(id: string, data: { name?: string; color?: string; textColor?: string; sortOrder?: number }) {
    const response = await this.api.put(`/api/categories/${id}`, data);
    return response.data;
  }

  async deleteCategory(id: string) {
    const response = await this.api.delete(`/api/categories/${id}`);
    return response.data;
  }

  // AI Feature APIs
  async getAIStatus() {
    const response = await this.api.get('/api/ai/status');
    return response.data;
  }

  async suggestSchedule(date: string, preferences?: any) {
    const response = await this.api.post('/api/ai/suggest-schedule', { date, preferences });
    return response.data;
  }

  async applyScheduleSuggestions(suggestions: any[]) {
    const response = await this.api.post('/api/ai/apply-schedule', { suggestions });
    return response.data;
  }

  async getDailyReflection(date?: string) {
    const url = date ? `/api/ai/daily-reflection?date=${date}` : '/api/ai/daily-reflection';
    const response = await this.api.get(url);
    return response.data;
  }

  async suggestPriority(title: string, description?: string, date?: string) {
    const response = await this.api.post('/api/ai/suggest-priority', { title, description, date });
    return response.data;
  }

  // Quick Capture APIs
  async getQuickCaptures(includeProcessed = false) {
    const response = await this.api.get(`/api/quick-capture?includeProcessed=${includeProcessed}`);
    return response.data;
  }

  async createQuickCapture(content: string, source = 'text') {
    const response = await this.api.post('/api/quick-capture', { content, source });
    return response.data;
  }

  async convertCaptureToTask(captureId: string, scheduledDate?: string, scheduledHour?: number, taskType = 'unscheduled') {
    const response = await this.api.post(`/api/quick-capture/${captureId}/convert`, { scheduledDate, scheduledHour, taskType });
    return response.data;
  }

  async deleteQuickCapture(id: string) {
    const response = await this.api.delete(`/api/quick-capture/${id}`);
    return response.data;
  }

  // Habit APIs
  async getHabits() {
    const response = await this.api.get('/api/habits');
    return response.data;
  }

  async createHabit(data: { name: string; icon?: string; color?: string; targetValue?: number; unit?: string; frequency?: string; frequencyDay?: number | null }) {
    const response = await this.api.post('/api/habits', data);
    return response.data;
  }

  async updateHabit(id: string, data: any) {
    const response = await this.api.put(`/api/habits/${id}`, data);
    return response.data;
  }

  async deleteHabit(id: string) {
    const response = await this.api.delete(`/api/habits/${id}`);
    return response.data;
  }

  async getHabitLogs(date?: string, startDate?: string, endDate?: string) {
    let url = '/api/habits/logs';
    const params = [];
    if (date) params.push(`date=${date}`);
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async logHabit(habitId: string, value: number, date?: string, notes?: string) {
    const response = await this.api.post(`/api/habits/${habitId}/log`, { date, value, notes });
    return response.data;
  }

  async getHabitStreaks() {
    const response = await this.api.get('/api/habits/streaks');
    return response.data;
  }

  // Subscription APIs
  async getSubscriptionStatus() {
    const response = await this.api.get('/api/subscription/status');
    return response.data;
  }

  async createStripeCheckout(plan: 'monthly' | 'yearly', originUrl: string) {
    const response = await this.api.post('/api/subscription/stripe/checkout', { plan, originUrl });
    return response.data;
  }

  async getStripeSessionStatus(sessionId: string) {
    const response = await this.api.get(`/api/subscription/stripe/session/${sessionId}`);
    return response.data;
  }

  async createRazorpaySubscription(plan: 'monthly' | 'yearly') {
    const response = await this.api.post('/api/subscription/razorpay/create', { plan });
    return response.data;
  }

  async verifyRazorpaySubscription(payload: any) {
    const response = await this.api.post('/api/subscription/razorpay/verify', payload);
    return response.data;
  }

  // Reflection APIs
  async getReflection(date?: string) {
    const url = date ? `/api/reflections?date=${date}` : '/api/reflections';
    const response = await this.api.get(url);
    return response.data;
  }

  async saveReflection(data: {
    date?: string;
    gratitude?: string;
    accomplishments?: string;
    challenges?: string;
    lessons?: string;
    tomorrowPriorities?: string;
    moodRating?: number;
    energyLevel?: number;
  }) {
    const response = await this.api.post('/api/reflections', data);
    return response.data;
  }

  async getReflectionPrompts() {
    const response = await this.api.get('/api/reflections/prompts');
    return response.data;
  }

  async getReflectionStats() {
    const response = await this.api.get('/api/reflections/stats');
    return response.data;
  }

  async deleteReflection(date: string) {
    const response = await this.api.delete(`/api/reflections/${date}`);
    return response.data;
  }

  // Meal Planning APIs
  async getMeals(startDate?: string, endDate?: string, date?: string) {
    let url = '/api/meals';
    const params = [];
    if (date) params.push(`date=${date}`);
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async saveMeal(data: { date: string; mealType: string; title: string; description?: string; ingredients?: string; calories?: number; prepTime?: number }) {
    const response = await this.api.post('/api/meals', data);
    return response.data;
  }

  async toggleMealComplete(id: string) {
    const response = await this.api.patch(`/api/meals/${id}/complete`);
    return response.data;
  }

  async deleteMeal(id: string) {
    const response = await this.api.delete(`/api/meals/${id}`);
    return response.data;
  }

  async copyMealsWeek(sourceStartDate: string, targetStartDate: string) {
    const response = await this.api.post('/api/meals/copy-week', { sourceStartDate, targetStartDate });
    return response.data;
  }

  // Cleaning APIs
  async getCleaningTasks() {
    const response = await this.api.get('/api/cleaning');
    return response.data;
  }

  async createCleaningTask(data: { name: string; area?: string; frequency?: string; estimatedMinutes?: number }) {
    const response = await this.api.post('/api/cleaning', data);
    return response.data;
  }

  async updateCleaningTask(id: string, data: any) {
    const response = await this.api.put(`/api/cleaning/${id}`, data);
    return response.data;
  }

  async deleteCleaningTask(id: string) {
    const response = await this.api.delete(`/api/cleaning/${id}`);
    return response.data;
  }

  async completeCleaningTask(id: string, date?: string, notes?: string) {
    const response = await this.api.post(`/api/cleaning/${id}/complete`, { date, notes });
    return response.data;
  }

  async getCleaningLogs(startDate?: string, endDate?: string) {
    let url = '/api/cleaning/logs';
    const params = [];
    if (startDate) params.push(`startDate=${startDate}`);
    if (endDate) params.push(`endDate=${endDate}`);
    if (params.length) url += `?${params.join('&')}`;
    const response = await this.api.get(url);
    return response.data;
  }

  async getCleaningSummary() {
    const response = await this.api.get('/api/cleaning/summary');
    return response.data;
  }

  // Family Goals APIs
  async getGoals(status?: string) {
    const url = status ? `/api/goals?status=${status}` : '/api/goals';
    const response = await this.api.get(url);
    return response.data;
  }

  async getGoal(id: string) {
    const response = await this.api.get(`/api/goals/${id}`);
    return response.data;
  }

  async createGoal(data: { title: string; description?: string; category?: string; targetDate?: string; targetValue?: number; unit?: string; priority?: number; color?: string }) {
    const response = await this.api.post('/api/goals', data);
    return response.data;
  }

  async updateGoal(id: string, data: any) {
    const response = await this.api.put(`/api/goals/${id}`, data);
    return response.data;
  }

  async deleteGoal(id: string) {
    const response = await this.api.delete(`/api/goals/${id}`);
    return response.data;
  }

  async addGoalProgress(id: string, data: { valueAdded?: number; notes?: string; date?: string }) {
    const response = await this.api.post(`/api/goals/${id}/progress`, data);
    return response.data;
  }

  async addGoalMilestone(id: string, data: { title: string; targetValue?: number }) {
    const response = await this.api.post(`/api/goals/${id}/milestones`, data);
    return response.data;
  }

  async toggleMilestoneComplete(milestoneId: string) {
    const response = await this.api.patch(`/api/goals/milestones/${milestoneId}/complete`);
    return response.data;
  }

  async deleteMilestone(milestoneId: string) {
    const response = await this.api.delete(`/api/goals/milestones/${milestoneId}`);
    return response.data;
  }

  // Routine APIs
  async getRoutines() {
    const response = await this.api.get('/api/routines');
    return response.data;
  }

  async getRoutine(id: string) {
    const response = await this.api.get(`/api/routines/${id}`);
    return response.data;
  }

  async createRoutine(data: { name: string; description?: string }) {
    const response = await this.api.post('/api/routines', data);
    return response.data;
  }

  async updateRoutine(id: string, data: any) {
    const response = await this.api.put(`/api/routines/${id}`, data);
    return response.data;
  }

  async deleteRoutine(id: string) {
    const response = await this.api.delete(`/api/routines/${id}`);
    return response.data;
  }

  async addRoutineItem(routineId: string, data: { title: string; description?: string; dayOfWeek?: number; startTime?: string; durationMinutes?: number; category?: string; color?: string }) {
    const response = await this.api.post(`/api/routines/${routineId}/items`, data);
    return response.data;
  }

  async updateRoutineItem(itemId: string, data: any) {
    const response = await this.api.put(`/api/routines/items/${itemId}`, data);
    return response.data;
  }

  async deleteRoutineItem(itemId: string) {
    const response = await this.api.delete(`/api/routines/items/${itemId}`);
    return response.data;
  }

  async getDayRoutine(dayOfWeek: number) {
    const response = await this.api.get(`/api/routines/day/${dayOfWeek}`);
    return response.data;
  }

  async applyRoutine(routineId: string, date: string) {
    const response = await this.api.post(`/api/routines/${routineId}/apply`, { date });
    return response.data;
  }

  // ==================== FOCUS TIME ====================
  async getFocusBlocks(date: string) {
    const response = await this.api.get(`/api/focus?date=${date}`);
    return response.data;
  }

  async createFocusBlock(data: { title: string; blockDate: string; startTime: string; endTime: string; isRecurring?: boolean; recurringDays?: number[]; color?: string }) {
    const response = await this.api.post('/api/focus', data);
    return response.data;
  }

  async deleteFocusBlock(id: string) {
    const response = await this.api.delete(`/api/focus/${id}`);
    return response.data;
  }

  async startFocusSession(blockId: string) {
    const response = await this.api.post('/api/focus/sessions/start', { blockId });
    return response.data;
  }

  async endFocusSession(sessionId: string) {
    const response = await this.api.post(`/api/focus/sessions/${sessionId}/end`);
    return response.data;
  }

  // ==================== TIME TRACKING ====================
  async getTimeTrackingSummary(startDate: string, endDate: string) {
    const response = await this.api.get(`/api/time-tracking/summary?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  }

  async startTimeTracking(taskId: string) {
    const response = await this.api.post('/api/time-tracking/start', { taskId });
    return response.data;
  }

  async stopTimeTracking(taskId: string) {
    const response = await this.api.post('/api/time-tracking/stop', { taskId });
    return response.data;
  }

  // ==================== SHOPPING LISTS ====================
  async getShoppingLists() {
    const response = await this.api.get('/api/shopping');
    return response.data;
  }

  async createShoppingList(data: { name: string }) {
    const response = await this.api.post('/api/shopping', data);
    return response.data;
  }

  async getShoppingList(id: string) {
    const response = await this.api.get(`/api/shopping/${id}`);
    return response.data;
  }

  async addShoppingItem(listId: string, data: { itemName: string; quantity?: number; unit?: string; category?: string; notes?: string }) {
    const response = await this.api.post(`/api/shopping/${listId}/items`, data);
    return response.data;
  }

  async updateShoppingList(id: string, data: { name: string }) {
    const response = await this.api.put(`/api/shopping/${id}`, data);
    return response.data;
  }

  async deleteShoppingList(id: string) {
    const response = await this.api.delete(`/api/shopping/${id}`);
    return response.data;
  }

  async toggleShoppingItem(itemId: string) {
    const response = await this.api.patch(`/api/shopping/items/${itemId}/toggle`);
    return response.data;
  }

  async deleteShoppingItem(itemId: string) {
    const response = await this.api.delete(`/api/shopping/items/${itemId}`);
    return response.data;
  }

  async generateShoppingFromMeals(weekStartDate: string) {
    const response = await this.api.post('/api/shopping/generate-from-meals', { weekStartDate });
    return response.data;
  }

  // ==================== FAMILY HUB ====================
  async getFamilyMembers() {
    const response = await this.api.get('/api/family/members');
    return response.data;
  }

  async createFamilyMember(data: { name: string; relationship?: string; birthdate?: string; color?: string; avatarEmoji?: string }) {
    const response = await this.api.post('/api/family/members', data);
    return response.data;
  }

  async getMemberWishlist(memberId: string) {
    const response = await this.api.get(`/api/family/members/${memberId}/wishlist`);
    return response.data;
  }

  async addWishlistItem(memberId: string, data: { itemName: string; description?: string; price?: number; link?: string; priority?: number; occasion?: string }) {
    const response = await this.api.post(`/api/family/members/${memberId}/wishlist`, data);
    return response.data;
  }

  async toggleWishlistPurchased(itemId: string) {
    const response = await this.api.patch(`/api/family/wishlist/${itemId}/toggle`);
    return response.data;
  }

  async getMemberSizes(memberId: string) {
    const response = await this.api.get(`/api/family/members/${memberId}/sizes`);
    return response.data;
  }

  async addMemberSize(memberId: string, data: { itemType: string; size: string; brand?: string }) {
    const response = await this.api.post(`/api/family/members/${memberId}/sizes`, data);
    return response.data;
  }

  // ==================== BUDGET GOALS ====================
  async getBudgetGoals() {
    const response = await this.api.get('/api/budget');
    return response.data;
  }

  async createBudgetGoal(data: { name: string; targetAmount: number; currency?: string; category?: string; deadline?: string; color?: string }) {
    const response = await this.api.post('/api/budget', data);
    return response.data;
  }

  async getBudgetGoal(id: string) {
    const response = await this.api.get(`/api/budget/${id}`);
    return response.data;
  }

  async updateBudgetGoal(id: string, data: { name?: string; targetAmount?: number; currency?: string; category?: string; deadline?: string; color?: string }) {
    const response = await this.api.put(`/api/budget/${id}`, data);
    return response.data;
  }

  async deleteBudgetGoal(id: string) {
    const response = await this.api.delete(`/api/budget/${id}`);
    return response.data;
  }

  async addBudgetTransaction(goalId: string, data: { amount: number; transactionType: string; description?: string }) {
    const response = await this.api.post(`/api/budget/${goalId}/transactions`, data);
    return response.data;
  }

  async getBudgetSummary() {
    const response = await this.api.get('/api/budget/summary/all');
    return response.data;
  }

  // ==================== WEATHER ====================
  async getWeather(lat: number, lon: number) {
    const response = await this.api.get(`/api/weather?lat=${lat}&lon=${lon}`);
    return response.data;
  }

  // ==================== TASK DEPENDENCIES ====================
  async getTaskDependencies(taskId: string) {
    const response = await this.api.get(`/api/dependencies/task/${taskId}`);
    return response.data;
  }

  async addTaskDependency(taskId: string, dependsOnTaskId: string) {
    const response = await this.api.post('/api/dependencies', { taskId, dependsOnTaskId });
    return response.data;
  }

  async removeTaskDependency(id: string) {
    const response = await this.api.delete(`/api/dependencies/${id}`);
    return response.data;
  }

  // ==================== GAMIFICATION ====================
  async getGamificationStats() {
    const response = await this.api.get('/api/gamification/stats');
    return response.data;
  }

  async getLeaderboard(period: string = 'week') {
    const response = await this.api.get(`/api/gamification/leaderboard?period=${period}`);
    return response.data;
  }

  async markNotificationsRead(notificationIds: string[]) {
    const response = await this.api.post('/api/gamification/notifications/read', { notificationIds });
    return response.data;
  }

  async updateStreak(streakType: string) {
    const response = await this.api.post('/api/gamification/streak/update', { streakType });
    return response.data;
  }

  async awardPoints(points: number, taskCompleted: boolean = false, habitCompleted: boolean = false) {
    const response = await this.api.post('/api/gamification/points/award', { points, taskCompleted, habitCompleted });
    return response.data;
  }

  // ==================== FAMILY DASHBOARD ====================
  async getFamilyDashboard() {
    const response = await this.api.get('/api/family-dashboard');
    return response.data;
  }

  async getFamilyActivity(limit: number = 20) {
    const response = await this.api.get(`/api/family-dashboard/activity?limit=${limit}`);
    return response.data;
  }

  // ==================== HEALTH DATA ====================
  async getHealthData(startDate?: string, endDate?: string) {
    let url = '/api/health';
    if (startDate || endDate) {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      url += '?' + params.toString();
    }
    const response = await this.api.get(url);
    return response.data;
  }

  async addHealthData(data: { dataDate: string; steps?: number | null; sleepHours?: number | null; sleepQuality?: string; activeMinutes?: number | null }) {
    const response = await this.api.post('/api/health', data);
    return response.data;
  }

  async getHealthIntegrations() {
    const response = await this.api.get('/api/health/integrations');
    return response.data;
  }

  async getHealthRecommendations() {
    const response = await this.api.get('/api/health/recommendations');
    return response.data;
  }

  // ==================== CALENDAR SYNC ====================
  async getCalendarStatus() {
    const response = await this.api.get('/api/calendar/status');
    return response.data;
  }

  async getCalendarEvents(startDate: string, endDate: string) {
    const response = await this.api.get(`/api/calendar/events?startDate=${startDate}&endDate=${endDate}`);
    return response.data;
  }

  async syncCalendar() {
    const response = await this.api.post('/api/calendar/sync');
    return response.data;
  }

  async disconnectCalendar(provider: string) {
    const response = await this.api.delete(`/api/calendar/disconnect/${provider}`);
    return response.data;
  }

  // =====================================================
  // COLLABORATIVE WORKSPACES
  // =====================================================

  async getWorkspaces() {
    const response = await this.api.get('/api/workspaces');
    return response.data;
  }

  async createWorkspace(data: { name: string; description?: string; color?: string; icon?: string }) {
    const response = await this.api.post('/api/workspaces', data);
    return response.data;
  }

  async getWorkspaceDetails(workspaceId: string) {
    const response = await this.api.get(`/api/workspaces/${workspaceId}`);
    return response.data;
  }

  async updateWorkspace(workspaceId: string, data: { name?: string; description?: string; color?: string; icon?: string }) {
    const response = await this.api.put(`/api/workspaces/${workspaceId}`, data);
    return response.data;
  }

  async deleteWorkspace(workspaceId: string) {
    const response = await this.api.delete(`/api/workspaces/${workspaceId}`);
    return response.data;
  }

  async inviteToWorkspace(workspaceId: string, email: string, role?: string) {
    const response = await this.api.post(`/api/workspaces/${workspaceId}/invite`, { email, role });
    return response.data;
  }

  async removeWorkspaceMember(workspaceId: string, memberId: string) {
    const response = await this.api.delete(`/api/workspaces/${workspaceId}/members/${memberId}`);
    return response.data;
  }

  async leaveWorkspace(workspaceId: string) {
    const response = await this.api.post(`/api/workspaces/${workspaceId}/leave`);
    return response.data;
  }

  async getWorkspaceTasks(workspaceId: string, date?: string, includeCompleted: boolean = false) {
    const parts: string[] = [];
    if (date) parts.push(`date=${date}`);
    if (includeCompleted) parts.push('includeCompleted=true');
    const params = parts.length ? `?${parts.join('&')}` : '';
    const response = await this.api.get(`/api/workspaces/${workspaceId}/tasks${params}`);
    return response.data;
  }

  async createWorkspaceTask(workspaceId: string, task: any) {
    const response = await this.api.post(`/api/workspaces/${workspaceId}/tasks`, task);
    return response.data;
  }

  async assignWorkspaceTask(workspaceId: string, taskId: string, assignedTo: string | null) {
    const response = await this.api.patch(`/api/workspaces/${workspaceId}/tasks/${taskId}/assign`, { assignedTo });
    return response.data;
  }

  async completeWorkspaceTask(workspaceId: string, taskId: string, completed: boolean) {
    const response = await this.api.patch(`/api/workspaces/${workspaceId}/tasks/${taskId}/complete`, { completed });
    return response.data;
  }

  async editWorkspaceTask(workspaceId: string, taskId: string, data: { title?: string; description?: string; scheduledDate?: string }) {
    const response = await this.api.put(`/api/workspaces/${workspaceId}/tasks/${taskId}`, data);
    return response.data;
  }

  async deleteWorkspaceTask(workspaceId: string, taskId: string) {
    const response = await this.api.delete(`/api/workspaces/${workspaceId}/tasks/${taskId}`);
    return response.data;
  }

  async reactToWorkspaceTask(workspaceId: string, taskId: string, reaction: string) {
    const response = await this.api.post(`/api/workspaces/${workspaceId}/tasks/${taskId}/react`, { reaction });
    return response.data;
  }

  async getWorkspaceActivity(workspaceId: string, limit?: number) {
    const params = limit ? `?limit=${limit}` : '';
    const response = await this.api.get(`/api/workspaces/${workspaceId}/activity${params}`);
    return response.data;
  }

  // Invitation handling (some don't require auth)
  async validateInvitation(token: string) {
    const response = await this.api.get(`/api/invite/validate/${token}`);
    return response.data;
  }

  async acceptInvitation(token: string) {
    const response = await this.api.post(`/api/invite/accept/${token}`);
    return response.data;
  }

  async registerViaInvitation(token: string, data: { firstName?: string; lastName?: string; password: string }) {
    const response = await this.api.post(`/api/invite/register/${token}`, data);
    return response.data;
  }

  async cancelInvitation(invitationId: string) {
    const response = await this.api.delete(`/api/workspaces/invitations/${invitationId}`);
    return response.data;
  }

  // =====================================================
  // TRAVEL PLANNER
  // =====================================================

  async getTrips(status?: string) {
    const params = status ? `?status=${status}` : '';
    const response = await this.api.get(`/api/travel${params}`);
    return response.data;
  }

  async createTrip(data: { name: string; destination?: string; startDate: string; endDate: string; notes?: string }) {
    const response = await this.api.post('/api/travel', data);
    return response.data;
  }

  async getTripDetails(tripId: string) {
    const response = await this.api.get(`/api/travel/${tripId}`);
    return response.data;
  }

  async updateTrip(tripId: string, data: any) {
    const response = await this.api.put(`/api/travel/${tripId}`, data);
    return response.data;
  }

  async deleteTrip(tripId: string) {
    const response = await this.api.delete(`/api/travel/${tripId}`);
    return response.data;
  }

  async uploadTripDocument(tripId: string, formData: FormData) {
    const response = await this.api.post(`/api/travel/${tripId}/documents`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000 // 2 minutes for AI processing
    });
    return response.data;
  }

  async deleteTripDocument(tripId: string, docId: string) {
    const response = await this.api.delete(`/api/travel/${tripId}/documents/${docId}`);
    return response.data;
  }

  async downloadTripDocument(tripId: string, docId: string): Promise<Blob> {
    const response = await this.api.get(`/api/travel/${tripId}/documents/${docId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  }

  async addTripItineraryItem(tripId: string, item: any) {
    const response = await this.api.post(`/api/travel/${tripId}/itinerary`, item);
    return response.data;
  }

  async updateTripItineraryItem(tripId: string, itemId: string, item: any) {
    const response = await this.api.put(`/api/travel/${tripId}/itinerary/${itemId}`, item);
    return response.data;
  }

  async deleteTripItineraryItem(tripId: string, itemId: string) {
    const response = await this.api.delete(`/api/travel/${tripId}/itinerary/${itemId}`);
    return response.data;
  }

  async generateTripSummary(tripId: string) {
    const response = await this.api.post(`/api/travel/${tripId}/generate-summary`);
    return response.data;
  }

  // =====================================================
  // HEALTH TRACKER (Weight & Documents)
  // =====================================================

  async getHealthProfile() {
    const response = await this.api.get('/api/health-tracker/profile');
    return response.data;
  }

  async saveHealthProfile(data: any) {
    const response = await this.api.post('/api/health-tracker/profile', data);
    return response.data;
  }

  async getWeightLogs(startDate?: string, endDate?: string, limit?: number) {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const response = await this.api.get(`/api/health-tracker/weight${queryString}`);
    return response.data;
  }

  async logWeight(data: { weightKg: number; logDate?: string; notes?: string }) {
    const response = await this.api.post('/api/health-tracker/weight', data);
    return response.data;
  }

  async deleteWeightLog(logId: string) {
    const response = await this.api.delete(`/api/health-tracker/weight/${logId}`);
    return response.data;
  }

  async getHealthDocuments() {
    const response = await this.api.get('/api/health-tracker/documents');
    return response.data;
  }

  async uploadHealthDocument(formData: FormData) {
    const response = await this.api.post('/api/health-tracker/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  async deleteHealthDocument(docId: string) {
    const response = await this.api.delete(`/api/health-tracker/documents/${docId}`);
    return response.data;
  }

  async getHealthSummary() {
    const response = await this.api.get('/api/health-tracker/summary');
    return response.data;
  }

  async getWeightReminderSettings() {
    const response = await this.api.get('/api/health-tracker/reminder-settings');
    return response.data;
  }

  async updateWeightReminderSettings(data: { enabled: boolean; time: string }) {
    const response = await this.api.post('/api/health-tracker/reminder-settings', data);
    return response.data;
  }

  // Location Reminders
  async getLocationReminders() {
    const response = await this.api.get('/api/location-reminders');
    return response.data;
  }

  async createLocationReminder(data: { name: string; address: string; latitude: number; longitude: number; radiusMeters: number; triggerOn: string; reminderText: string }) {
    const response = await this.api.post('/api/location-reminders', data);
    return response.data;
  }

  async updateLocationReminder(id: string, data: { isActive?: boolean }) {
    const response = await this.api.patch(`/api/location-reminders/${id}`, data);
    return response.data;
  }

  async deleteLocationReminder(id: string) {
    const response = await this.api.delete(`/api/location-reminders/${id}`);
    return response.data;
  }

  async getSavedLocations() {
    const response = await this.api.get('/api/saved-locations');
    return response.data;
  }

  async createSavedLocation(data: { name: string; address: string; latitude: number; longitude: number }) {
    const response = await this.api.post('/api/saved-locations', data);
    return response.data;
  }

  async deleteSavedLocation(id: string) {
    const response = await this.api.delete(`/api/saved-locations/${id}`);
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
